import routes from '@/config/routes';
import { Product } from '@/types';
import ImageComponent from '../ui/image';
import AnchorLink from '../ui/links/anchor-link';
import placeholder from '@/assets/images/placeholders/product.svg';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';
import { LightningIcon } from '../icons/lightning-icon';
import { useNSFWBlur } from '@/lib/hooks/use-nsfw-blur';
import { cn } from '@/lib/utils';
import NSFWToggle from '../nsfw/nsfw-toggle';
import FavoriteButton from '../favorite/favorite-button';
import { useEffect, useState } from 'react';
import { getAIModelIcon } from '@/lib/get-icon';
import RatingsBadge from '../review/rating-badge';

export default function Card({ product }: { product: Product }) {
  const { shouldBlur } = useNSFWBlur(product);
  const {
    name,
    slug,
    image,
    ai_model,
    ai_model_version_slug,
    type_id,
    shop,
    store_review_amount,
    store_review_score,
  } = product ?? {};

  const [isSquareImage, setIsSquareImage] = useState(false);
  const [isLandscapeImage, setIsLandscapeImage] = useState(false);
  const [isWideImage, setIsWideImage] = useState(false);

  // Detect image dimensions to determine aspect ratio
  useEffect(() => {
    if (image?.original) {
      const img = new Image();
      img.src = image.original;
      img.onload = () => {
        if (img.width === img.height) {
          setIsSquareImage(true);
          setIsLandscapeImage(false);
          setIsWideImage(false);
        } else if (img.width > img.height) {
          setIsSquareImage(false);
          setIsLandscapeImage(true);
          // Check if it's a very wide image (more than 1.5x wider than tall)
          setIsWideImage(img.width / img.height > 1.5);
        } else {
          setIsSquareImage(false);
          setIsLandscapeImage(false);
          setIsWideImage(false);
        }
      };
    }
  }, [image?.original]);

  return (
    <div className="relative flex h-full max-w-[300px] flex-col">
      <div className="relative">
        <AnchorLink
          href={routes.productUrl(slug)}
          className={cn(
            shouldBlur && 'pointer-events-none',
            shouldBlur && 'blur-md'
          )}
        >
          <motion.div
            className={cn(
              'group relative overflow-hidden rounded-lg bg-white shadow dark:bg-black',
              isSquareImage
                ? 'aspect-square'
                : isLandscapeImage
                ? isWideImage
                  ? 'aspect-[5/4]'
                  : 'aspect-square' // Make landscape images closer to square
                : 'aspect-[3/4]'
            )}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{
              scale: 1.02,
              boxShadow:
                '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
          >
            <div
              className={cn(
                'relative h-full w-full',
                isLandscapeImage && 'flex items-center justify-center'
              )}
            >
              <ImageComponent
                width={344}
                height={344}
                alt={`${ai_model?.name} ${ai_model_version_slug} Model for ${name}`}
                title={`View ${ai_model?.name} Model for ${name}`}
                className={cn(
                  'h-full w-full',
                  isSquareImage
                    ? 'object-cover'
                    : isLandscapeImage
                    ? 'max-h-full object-contain'
                    : 'object-cover',
                  shouldBlur && 'blur-md'
                )}
                src={image?.original ?? placeholder}
                loading="lazy"
              />
            </div>

            {/* Subtle gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 duration-300 transition-opacity group-hover:opacity-100" />
          </motion.div>
        </AnchorLink>
      </div>

      {/* Content below the image */}
      <div className="flex flex-1 flex-col justify-end p-3">
        {/* AI Model info */}
        {ai_model && (
          <div className="mb-1 flex items-center gap-2">
            {getAIModelIcon({
              iconName: ai_model.slug,
              className: 'h-4 w-4 text-light-base',
              type_id: type_id?.toString(),
            })}
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {ai_model.name} <span className="hidden md:inline">Model</span>
            </span>
          </div>
        )}
        {/* Product name and favorite */}
        <div className="flex items-center justify-between">
          <h3 className="mb-1 truncate text-left text-sm font-semibold">
            {name}
          </h3>
          <div onClick={(e) => e.preventDefault()}>
            <FavoriteButton
              productId={product.id}
              size="xs"
              iconOnly
              className="!border-gray-200 !bg-transparent text-gray-500 hover:!bg-gray-100 hover:text-brand dark:!border-gray-800 dark:hover:!bg-gray-800"
            />
          </div>
        </div>
        {/* Shop name and reviews */}
        <div className="mt-1 flex flex-col gap-1">
          {store_review_amount > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="flex items-center">
                <RatingsBadge
                  rating={store_review_score}
                  className="!mx-0 !mr-1 h-2.5 !px-0"
                />
                ({store_review_amount})
              </span>
            </div>
          )}
          {shop?.name && (
            <div className="text-xs text-gray-500">{shop.name}</div>
          )}
        </div>
      </div>

      <div className="mt-auto p-3">
        <Button
          className="group relative h-10 w-full overflow-hidden rounded-md bg-gradient-to-r from-brand to-brand-dark duration-300 transition-all hover:from-brand-dark hover:to-brand"
          size="default"
        >
          <AnchorLink href={routes.productUrl(slug)}>
            <div className="relative z-10 flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-1">
                <LightningIcon className="mr-1.5 h-4 w-4 text-white" />
                <span className="text-xs font-medium text-white">
                  Try Model
                </span>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-white duration-300 transition-transform group-hover:translate-x-0.5" />
            </div>
          </AnchorLink>
        </Button>
      </div>

      {shouldBlur && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 backdrop-blur-md">
          <div className="bg-dot-pattern absolute inset-0 opacity-5" />
          <div className="relative flex flex-col items-center gap-2">
            <div className="mb-1 h-1.5 w-12 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
            <p className="text-sm font-medium text-foreground/90">
              Adult Content
            </p>
            <div className="rounded-lg border border-violet-500/20 bg-background/90 px-4 py-2 shadow-lg backdrop-blur-sm">
              <NSFWToggle size="lg" className="min-w-[120px]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
